import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { resendVerificationEmail } from "../lib/api";
import { MailWarning, X } from "lucide-react";

const RESEND_COOLDOWN_SECONDS = 60;

export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!user || user.email_verified || dismissed) return null;

  const handleResend = async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setFeedback("");
    try {
      await resendVerificationEmail();
      setFeedback("Verification email sent. Check your inbox.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to send. Try again in a minute.";
      setFeedback(detail);
      const m = String(detail).match(/(\d+)\s*seconds?/);
      if (m) setCooldown(parseInt(m[1], 10));
    }
    setSending(false);
  };

  return (
    <div
      data-testid="email-verification-banner"
      className="bg-amber-50 border-b border-amber-200 px-5 md:px-12 py-3"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <MailWarning className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm">
            <span className="font-semibold text-amber-900">Verify your email </span>
            <span className="text-amber-800">
              to create listings, message landlords, schedule showings, and pay securely.
            </span>
            {feedback && (
              <span className="block text-xs text-amber-700 mt-1" data-testid="banner-feedback">
                {feedback}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto sm:ml-0">
          <button
            data-testid="banner-resend-btn"
            onClick={handleResend}
            disabled={sending || cooldown > 0}
            className="text-xs font-semibold px-4 py-2 rounded-full bg-amber-900 text-white hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            {sending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
          </button>
          <button
            data-testid="banner-dismiss-btn"
            onClick={() => setDismissed(true)}
            className="p-1.5 rounded-full hover:bg-amber-100 text-amber-900"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
